import { motion } from 'framer-motion';
import { Nav } from './Nav';
import { Footer } from './Footer';

export const PolicyInfo = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <div className="relative overflow-hidden">
        <Nav />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
      
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="text-center py-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold leading-tight text-gray-900">
            Privacy Policy
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Gomens is committed to protecting the privacy of your information. This Privacy Policy outlines how we collect, use, disclose, and safeguard your Personal Information.
          </p>
        </motion.div>

        {/* Content Sections */}
        <div className="space-y-10">
          {/* Section 1: Information We Collect */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.2, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              1. Information We Collect
            </h2>
            <p className="text-gray-600">
              We collect various types of information, including:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 text-gray-600">
              <li>
                <strong>Personal Information:</strong> This includes information that can be used to identify you, such as your name, email address, billing address, and payment information. We collect this information when you register for an account, subscribe to our Service, contact us, or participate in surveys or promotions.
              </li>
              <li>
                <strong>Usage Data:</strong> We collect information about how you use the Site and the Service, such as your IP address, browser type, operating system, pages visited, time spent on pages, and other diagnostic data.
              </li>
              <li>
                <strong>Cookies and Tracking Technologies:</strong> We use cookies, web beacons, and similar technologies to collect information about your browsing activity and preferences. You can control the use of cookies through your browser settings.
              </li>
              <li>
                <strong>Customer Data:</strong> As a SaaS provider, we process data that you input or upload into our Service. This data is considered confidential and is processed according to your instructions and our contractual obligations.
              </li>
              <li>
                <strong>Third-Party Information:</strong> We may receive information about you from third-party sources, such as social media platforms or marketing partners, if you choose to connect your account or interact with us through these platforms.
              </li>
            </ul>
          </motion.section>

          {/* Section 2: How We Use Your Information */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.3, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              2. How We Use Your Information
            </h2>
            <p className="text-gray-600">
              We use your information for various purposes, including:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 text-gray-600">
              <li>
                <strong>Providing and Maintaining the Service:</strong> To create and manage your account, process payments, provide customer support, and personalize your experience.
              </li>
              <li>
                <strong>Improving the Service:</strong> To analyze usage data, troubleshoot technical issues, and develop new features and functionalities.
              </li>
              <li>
                <strong>Communicating with You:</strong> To send you updates, newsletters, marketing materials, and other information about our products and services. You can opt out of receiving marketing communications at any time.
              </li>
              <li>
                <strong>Protecting Our Rights and Interests:</strong> To enforce our terms of service, prevent fraud, and comply with legal obligations.
              </li>
              <li>
                <strong>Processing Customer Data:</strong> To provide the SaaS services requested by our customers. We will only use Customer Data as authorized by our customers in accordance with our contractual obligations.
              </li>
              <li>
                <strong>Legal Compliance:</strong> To comply with applicable laws and regulations, including responding to legal requests and court orders.
              </li>
            </ul>
          </motion.section>

          {/* Section 3: Sharing Your Information */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.4, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              3. Sharing Your Information
            </h2>
            <p className="text-gray-600">
              We may share your information with:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 text-gray-600">
              <li>
                <strong>Service Providers:</strong> We may share your information with third-party service providers who assist us with payment processing, data storage, analytics, marketing, and other services.
              </li>
              <li>
                <strong>Business Partners:</strong> We may share your information with business partners who offer complementary products or services.
              </li>
              <li>
                <strong>Legal Authorities:</strong> We may disclose your information to legal authorities or government agencies if required by law or to protect our rights and interests.
              </li>
              <li>
                <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring company.
              </li>
              <li>
                <strong>With your Consent:</strong> We may share your information with third parties with your consent.
              </li>
              <li>
                <strong>Customer Directed Sharing:</strong> We will share customer data as directed by the customer as part of the normal operation of the SaaS service.
              </li>
            </ul>
          </motion.section>

          {/* Section 4: Data Security */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.5, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              4. Data Security
            </h2>
            <p className="text-gray-600">
              We implement reasonable security measures to protect your information from unauthorized access, disclosure, or alteration. However, no method of transmission over the internet or electronic storage is 100% secure.
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 text-gray-600">
              <li>
                <strong>Encryption:</strong> We use industry-standard encryption to protect sensitive data during transmission.
              </li>
              <li>
                <strong>Access Controls:</strong> We restrict access to your information to authorized personnel only.
              </li>
              <li>
                <strong>Regular Security Audits:</strong> We conduct regular security audits to identify and address vulnerabilities.
              </li>
              <li>
                <strong>Data Minimization:</strong> We only collect and retain the data necessary to provide our service.
              </li>
            </ul>
          </motion.section>

          {/* Section 5: Data Retention */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.6, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              5. Data Retention
            </h2>
            <p className="text-gray-600">
              We retain your Personal Information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Customer Data retention is dictated by the agreements in place with each customer.
            </p>
          </motion.section>

          {/* Section 6: Your Rights */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.7, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              6. Your Rights
            </h2>
            <p className="text-gray-600">
              You have the following rights regarding your Personal Information:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 text-gray-600">
              <li>
                <strong>Access:</strong> You can request access to the Personal Information we hold about you.
              </li>
              <li>
                <strong>Correction:</strong> You can request to correct any inaccuracies in your Personal Information.
              </li>
              <li>
                <strong>Deletion:</strong> You can request to delete your Personal Information, subject to certain exceptions.
              </li>
              <li>
                <strong>Restriction:</strong> You can request to restrict the processing of your Personal Information.
              </li>
              <li>
                <strong>Portability:</strong> You can request to receive your Personal Information in a portable format.
              </li>
              <li>
                <strong>Objection:</strong> You can object to the processing of your Personal Information.
              </li>
              <li>
                <strong>Withdrawal of Consent:</strong> If we rely on your consent to process your Personal Information, you can withdraw your consent at any time.
              </li>
              <li>
                <strong>California Residents:</strong> California residents have additional rights under the California Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA), including the right to opt-out of the sale or sharing of their personal information and the right to limit the use of sensitive personal information.
              </li>
              <li>
                <strong>GDPR:</strong> If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR), including the right to lodge a complaint with a supervisory authority.
              </li>
            </ul>
          </motion.section>

          {/* Section 7: Children's Privacy */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.8, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              7. Children's Privacy
            </h2>
            <p className="text-gray-600">
              Our Service is not intended for children under the age of 16. We do not knowingly collect Personal Information from children under 16. If we become aware that we have collected Personal Information from a child under 16, we will take steps to delete it.
            </p>
          </motion.section>

          {/* Section 8: Changes to This Privacy Policy */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.9, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              8. Changes to This Privacy Policy
            </h2>
            <p className="text-gray-600">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page. Your continued use of the Site or the Service after any changes constitutes your acceptance of the revised Privacy Policy.
            </p>
          </motion.section>

          {/* Section 9: Contact Us */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 1.0, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              9. Contact Us
            </h2>
            <p className="text-gray-600">
              If you have any questions or concerns about this Privacy Policy, please contact us at:
            </p>
            <p className="mt-4 text-gray-600">
              Gomens<br />
              <a href="mailto:info@gomens.ai" className="text-indigo-600 hover:text-indigo-500">info@gomens.ai</a>
            </p>
          </motion.section>

          {/* Section 10: Specific Regional Compliance */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 1.1, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              10. Specific Regional Compliance
            </h2>
            <p className="text-gray-600">
              This policy is intended to comply with global privacy standards, including but not limited to GDPR, CCPA/CPRA, and other applicable laws. We implement specific measures for each region as required.
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 text-gray-600">
              <li>
                <strong>GDPR Compliance:</strong> We have implemented data protection impact assessments (DPIAs), appointed a Data Protection Officer (DPO) (if required), and established mechanisms for data subject requests.
              </li>
              <li>
                <strong>CCPA/CPRA Compliance:</strong> We provide clear notice of data collection practices, offer opt-out rights for the sale or sharing of personal information, and implement procedures for verifying and responding to consumer requests.
              </li>
            </ul>
          </motion.section>

          {/* Section 11: International Data Transfers */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 1.2, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              11. International Data Transfers
            </h2>
            <p className="text-gray-600">
              If we transfer your information to countries outside of your jurisdiction, we will implement appropriate safeguards to ensure that your information is protected in accordance with this Privacy Policy and applicable laws.
            </p>
          </motion.section>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 -mt-20 -mr-20 hidden lg:block">
          <svg width="404" height="404" fill="none" viewBox="0 0 404 404">
            <defs>
              <pattern
                id="pattern-circles"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="2"
                  fill="rgba(124, 58, 237, 0.1)"
                />
              </pattern>
            </defs>
            <rect width="404" height="404" fill="url(#pattern-circles)" />
          </svg>
        </div>
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 hidden lg:block">
          <svg width="404" height="404" fill="none" viewBox="0 0 404 404">
            <defs>
              <pattern
                id="pattern-circles-2"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="2"
                  fill="rgba(124, 58, 237, 0.1)"
                />
              </pattern>
            </defs>
            <rect width="404" height="404" fill="url(#pattern-circles-2)" />
          </svg>
        </div>
        
      </div>
      <Footer />
    </div>
  );
};